import { useProviderVettingStatus } from '../hooks/useProviderVettingStatus';
import { VettingStatusBadge } from './VettingStatusBadge';
import type { ProviderVettingSummary } from '../types/vetting.types';

type ProviderVettingStatusCellProps = {
  providerId: string;
  /** Pre-fetched summary from a parent's batch call. Only meaningful when `fromBatch` is true. */
  summary?: ProviderVettingSummary;
  /**
   * True when a parent supplies `summary` via a batch call (e.g.
   * useProviderVettingStatusBatch in a table with many rows) — this cell then
   * NEVER does its own fetch, even before the batch resolves and `summary` is
   * still undefined (avoids a one-render flash of an individual request that
   * would defeat the whole point of batching). Omit for standalone usage,
   * where the cell fetches its own data.
   */
  fromBatch?: boolean;
};

/**
 * Single-badge cell for table rows (ProviderTable's "Background Check" column).
 * Split out from VettingCheckList because rows are rendered inside a `.map()` —
 * hooks can't be called there directly, so each row gets its own tiny component.
 */
export function ProviderVettingStatusCell({
  providerId,
  summary: providedSummary,
  fromBatch = false,
}: ProviderVettingStatusCellProps) {
  // Always call the hook (rules of hooks) — `fromBatch` alone (not whether
  // `summary` has arrived yet) decides whether this cell is allowed to fetch
  // on its own, so there's no race where an unresolved batch briefly falls
  // back to per-row requests.
  const fetchedSummary = useProviderVettingStatus(fromBatch ? undefined : providerId);
  const summary = fromBatch ? providedSummary : fetchedSummary;

  if (!summary || summary.checks.length === 0) return <span className="text-xs text-gray-400">—</span>;

  const check = summary.checks[0];
  return <VettingStatusBadge status={check.status} result={check.result} resolution={check.resolution} />;
}
